import React, { useRef, useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import WorkflowApi from "../../api/WorkflowApi";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import { validateEnclosedField } from "../../utils/validators";
import { getEntitlementsSection } from "../../utils/entitlements";
import { useProductBuilderMode } from "../../utils/productBuilderMode";
import { filterWorkflowAllowedObjects } from "../../utils/standardObjectPublishing";
import WorkflowCriteriaConfig from "./WorkflowCriteriaConfig";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Card,
  Badge,
  Collapse,
  Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import WorkflowCreateTask from "./WorkflowCreateTask";
import WorkflowSendEmail, { buildLegacyEmailSequence } from "./WorkflowSendEmail";
import WorkflowSendWhatsApp from "./WorkflowSendWhatsApp";
import WorkflowFieldUpdate from "./WorkflowFieldUpdate";
import WorkflowCreateRecord from "./WorkflowCreateRecord";
import WorkflowSendNotification from "./WorkflowSendNotification";
import WorkflowRunCustomScript from "./WorkflowRunCustomScript";
import WorkflowAttachmentSelector from "./WorkflowAttachmentSelector";
import AppToast from "./../AppToast";
import AttachmentApi from "../../api/AttachmentApi";
import { FaBolt, FaArrowLeft, FaSave, FaTrash, FaEdit, FaInfoCircle, FaPlus, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { standardInputStyles, standardLabelStyles } from "../../utils/conditionInputStyles";
import MultiSelectDropdown from "../layout/MultiSelectDropdown1";
import { useConditionValueFields } from "../../hooks/useConditionValueFields";
import { FILTER_VALUE_TYPE_STATIC } from "../../utils/filterFieldValueUtils";

function isSimpleWorkflowCriteriaLogic(logic) {
  const u = String(logic ?? "").trim().toUpperCase();
  return u === "AND" || u === "OR";
}

function defaultWorkflowCustomCriteriaExpression(validCriteriaCount) {
  if (validCriteriaCount <= 0) return "1";
  if (validCriteriaCount === 1) return "1";
  return Array.from({ length: validCriteriaCount }, (_, i) => String(i + 1)).join(
    " AND "
  );
}

/** Must match DB column size for `workflow.logic` (varchar(255)). */
const WORKFLOW_LOGIC_MAX_LENGTH = 255;

/** Persists as workflow.trigger_event: before_save | after_save (legacy: before/after, config.eventTiming). */
function normalizeTriggerEventForUi(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "before_save" || v === "before") return "before_save";
  if (v === "after_save" || v === "after") return "after_save";
  return "after_save";
}

export default function WorkflowEdit() {
  const { id } = useParams(); // <- UUID from route param
  console.log("UUID param:", id);
  const navigate = useNavigate();
  const location = useLocation();
  const productBuilderMode = useProductBuilderMode();

  const [workflow, setWorkflow] = useState({
    id: null,
    name: "",
    objectName: "",
    eventType: [], // Changed to array for multi-select
    triggerEvent: "after_save", // workflow.trigger_event
    isActive: true,
    logic: "AND",
    executionMode: "immediate", // "immediate" or "scheduled"
    scheduledType: "based_on_field", // Always use "based_on_field"
    scheduledDateTime: "",
    scheduledFieldName: "",
    scheduledExecution: "after", // "before" or "after"
    scheduledUnit: "days", // "minutes", "hours", or "days"
    scheduledValue: "",
  });
  const [readOnly, setReadonly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [validated, setValidated] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");
  // Inside WorkflowForm component, add state:
  const [objectConfigs, setObjectConfigs] = useState([]);
  const [criteriaList, setCriteriaList] = useState([]);
  const [objectFields, setObjectFields] = useState([]);
  const { valueFields: criteriaValueFields } = useConditionValueFields(workflow.objectName);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [objectActionCache, setObjectActionCache] = useState({});
  const [expandedActions, setExpandedActions] = useState({}); // Track which actions are expanded
  /** trigger_event at load time; After Save workflows cannot switch to Before Save on edit. */
  const [savedTriggerEvent, setSavedTriggerEvent] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const [toast, setToast] = useState({
    message: "",
    show: false,
    variant: "success",
  });

  const workflowEnt = getEntitlementsSection("workflow", {});

  // Redirect if no view permission, or create page without create permission
  useEffect(() => {
    if (workflowEnt.allowViewWorkflow === false) {
      navigate("/setup/workflows", { replace: true });
      return;
    }
    if (!id && workflowEnt.allowCreateWorkflow === false) {
      navigate("/setup/workflows", { replace: true });
      return;
    }
  }, [id, navigate, workflowEnt.allowViewWorkflow, workflowEnt.allowCreateWorkflow]);

  useEffect(() => {
    console.log("useEffect", id);
    // Set readonly: view-only path, or has id but no create permission (view only, no edit)
    const isEditOrCreate = location.pathname.endsWith("/edit") || location.pathname.endsWith("/create");
    const canEdit = workflowEnt.allowCreateWorkflow !== false;
    setReadonly(!isEditOrCreate || (id && id !== "create" && !canEdit));

    loadObjectConfigs(); // fetch object configs
    if (!id || id === "create") {
      setSavedTriggerEvent(null);
    }
    if (id && id !== "create") {
      loadWorkflow(id);
    }
    if (location.state?.successMessage) {
      triggerToast(location.state.successMessage);
    }
  }, [id, location.pathname, location.state, workflowEnt.allowCreateWorkflow, productBuilderMode]);

  /** Before Save: single Field Update action only (UI). */
  useEffect(() => {
    if (workflow.triggerEvent !== "before_save") return;
    setActions((prev) => {
      if (!prev || prev.length === 0) {
        return [{ actionType: "Field Update", config: {} }];
      }
      if (prev.length > 1) {
        const first = prev[0];
        return [
          {
            ...first,
            actionType: "Field Update",
            config: first.actionType === "Field Update" ? first.config : {},
          },
        ];
      }
      const only = prev[0];
      if (only.actionType === "Field Update") return prev;
      return [{ actionType: "Field Update", config: {} }];
    });
  }, [workflow.triggerEvent, workflow.objectName]);

  /**
   *
   */

  const addCriteria = () => {
    setCriteriaList((prev) =>
      Array.isArray(prev)
        ? [
          ...prev,
          {
            fieldName: "",
            operator: "",
            value: "",
            valueType: FILTER_VALUE_TYPE_STATIC,
            evaluateOn: workflow.eventType === "onCreate" ? "Always" : "Always"
          },
        ]
        : [{
          fieldName: "",
          operator: "",
          value: "",
          valueType: FILTER_VALUE_TYPE_STATIC,
          evaluateOn: workflow.eventType === "onCreate" ? "Always" : "Always"
        }]
    );
  };

  const deleteCriteria = (index) => {
    setCriteriaList((prev) => prev.filter((_, i) => i !== index));
  };
  const triggerToast = (message, variant = "success") => {
    setToast({
      message,
      show: true,
      variant,
    });
  };

  const showError = (message) => {
    setToast({
      message,
      show: true,
      variant: "danger",
    });
  };
  const patchCriteria = (index, patch) => {
    setCriteriaList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...patch };
      return updated;
    });
  };

  const updateCriteria = (index, key, value) => {
    const patch = { [key]: value };

    if (key === "operator") {
      const op = String(value || "").toLowerCase().replace(/\s+/g, "_");
      if (value === "isChanged") {
        patch.evaluateOn = "OnChange";
      }
      if (op.startsWith("old_is_null") || op.startsWith("old_is_not_null")) {
        patch.value = "";
      }
    }

    patchCriteria(index, patch);
  };
  /*  END Criteria Functions*/
  const loadObjectConfigs = async () => {
    try {
      console.log("Loading object configs...");
      const response = await ObjectConfigApi.fetchObjectConfigs();
      console.log("fetchObjectConfigs response: ", response);
      if (response.success && response.data) {
        const allowed = filterWorkflowAllowedObjects(response.data, { productBuilderMode });
        setObjectConfigs(allowed);
      } else {
        setObjectFields([]); // ✅ fallback to empty
      }
    } catch (err) {
      console.error("Failed to load object configs", err);
    }
  };

  const loadWorkflow = async (id) => {
    console.log("loadWorkflow", id);
    setLoading(true);
    try {
      const response = await WorkflowApi.fetchWorkflows(id); // Replace with your actual API call
      console.log("response --> ", response);
      if (response.success && response.data && response.data.length > 0) {
        const fetchedWorkflow = response.data[0];

        // Load scheduling config from workflow.config
        const schedulingConfig = fetchedWorkflow.config?.scheduling || {};
        // Parse event_type - can be string (single) or comma-separated (multiple)
        const eventTypeValue = fetchedWorkflow.event_type || "";
        const eventTypeArray = typeof eventTypeValue === 'string'
          ? (eventTypeValue.includes(',') ? eventTypeValue.split(',').map(e => e.trim()) : [eventTypeValue].filter(e => e))
          : (Array.isArray(eventTypeValue) ? eventTypeValue : []);

        const loadedTriggerEvent = normalizeTriggerEventForUi(
          fetchedWorkflow.trigger_event ?? fetchedWorkflow.config?.eventTiming
        );
        setSavedTriggerEvent(loadedTriggerEvent);
        setWorkflow({
          id: fetchedWorkflow.id,
          name: fetchedWorkflow.workflow_name,
          objectName: fetchedWorkflow.object_name,
          eventType: eventTypeArray,
          triggerEvent: loadedTriggerEvent,
          isActive: fetchedWorkflow.is_active,
          logic: fetchedWorkflow.logic || "AND",
          executionMode: schedulingConfig.executionMode || "immediate",
          scheduledType: schedulingConfig.scheduledType || "based_on_field",
          scheduledDateTime: schedulingConfig.scheduledDateTime || "",
          scheduledFieldName: schedulingConfig.scheduledFieldName || "",
          scheduledExecution: schedulingConfig.scheduledExecution || "after",
          scheduledUnit: schedulingConfig.scheduledUnit || "days",
          scheduledValue: schedulingConfig.scheduledValue || "",
        });
        console.log(
          "Fetching Object Fields for object: ",
          fetchedWorkflow.objectName
        );
        // Immediately load object fields
        if (fetchedWorkflow.object_name) {
          try {
            console.log(
              "Fetching Object Fields for object: ",
              fetchedWorkflow.object_name
            );
            const response =
              await ObjectConfigApi.fetchConfigObjectByNameWithFields(
                fetchedWorkflow.object_name
              );

            if (response.success && response.data?.fields) {
              console.log("Object Config Fields: ", response.data.fields);
              setObjectFields(response.data.fields);
            } else {
              setObjectFields([]);
              console.log("No Object Config Fields Found! ");
            }
          } catch (err) {
            console.error("Error fetching fields for object", err);
            setObjectFields([]);
          }
        } else {
          console.error("No data found for object config");
        }

        if (fetchedWorkflow.config?.actions) {
          setActions(fetchedWorkflow.config.actions);
          setCriteriaList(fetchedWorkflow.config.criteria);
        }
      }
    } catch (error) {
      console.error("Failed to load workflow", error);
    } finally {
      setLoading(false);
    }
  };

  const actionTypes = [
    "Send Email",
    "Send WhatsApp",
    "Create Task",
    "Create Record",
    "Field Update",
    "Send Notification",
    "Run Custom Script",
  ];

  const isBeforeSaveWorkflow = workflow.triggerEvent === "before_save";
  const actionTypeOptions = isBeforeSaveWorkflow ? ["Field Update"] : actionTypes;
  /** Existing After Save workflows cannot be changed to Before Save. */
  const blockBeforeSaveOnEdit = savedTriggerEvent === "after_save";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!workflow.name || !workflow.objectName || !workflow.eventType) {
      showError("Please fill all required fields.");
      return;
    }
    setValidated(true);

    console.log("Workflow:", workflow);
    console.log("Actions:", actions);
    // TODO: API call to save workflow and actions
  };

  const updateAttachmentConfig = (index, key, value) => {
    const updated = [...actions];
    updated[index].attachmentConfig = {
      ...updated[index].attachmentConfig,
      [key]: value,
    };
    setActions(updated);
  };

  // Upload pending attachments that were selected before workflow was saved
  const uploadPendingAttachments = async (workflowId, actionsSource, currentWorkflow) => {
    const targetActions = actionsSource || actions;
    let updatedActions = [...targetActions];
    let actionsModified = false;

    for (const [index, action] of targetActions.entries()) {
      const isWhatsApp = action.actionType === "Send WhatsApp";
      const attConfig = action.attachmentConfig || action.config?.attachmentConfig;
      const files = attConfig?.staticFiles;

      if (files && files.length > 0) {
        try {
          const fileList = Array.isArray(files) ? files : [files];
          const response = await AttachmentApi.uploadWorkflowAttachments(
            workflowId,
            index,
            fileList,
            attConfig?.description || ''
          );

          if (response?.success && response?.data) {
            const uploadedFile = response.data[0];
            const newAttachmentIds = response.data.map((f) => f.id);
            const uploadedFileName = uploadedFile?.file_name || fileList[0]?.name;

            actionsModified = true;
            if (isWhatsApp) {
              updatedActions[index] = {
                ...updatedActions[index],
                config: {
                  ...updatedActions[index].config,
                  attachmentConfig: {
                    ...updatedActions[index].config?.attachmentConfig,
                    fileName: uploadedFileName || updatedActions[index].config?.attachmentConfig?.fileName,
                    attachmentIds: newAttachmentIds,
                    staticFiles: null,
                  },
                },
              };
            } else {
              updatedActions[index] = {
                ...updatedActions[index],
                attachmentConfig: {
                  ...updatedActions[index].attachmentConfig,
                  fileName: uploadedFileName || updatedActions[index].attachmentConfig?.fileName,
                  attachmentIds: newAttachmentIds,
                  staticFiles: null,
                },
              };
            }
          }
        } catch (error) {
          console.error(`Error uploading attachments for action ${index}:`, error);
          // Don't fail the entire save if attachment upload fails
        }
      }
    }

    if (actionsModified) {
      setActions(updatedActions);
      if (currentWorkflow) {
        try {
          const updatedPayload = {
            id: workflowId,
            name: currentWorkflow.name,
            objectName: currentWorkflow.objectName,
            eventType: currentWorkflow.eventType,
            triggerEvent: currentWorkflow.triggerEvent,
            isActive: currentWorkflow.isActive,
            logic: currentWorkflow.logic,
            config: {
              actions: updatedActions,
              criteria: criteriaList,
              scheduling: {
                executionMode: currentWorkflow.executionMode,
                scheduledType: currentWorkflow.scheduledType,
                scheduledDateTime: currentWorkflow.scheduledDateTime,
                scheduledFieldName: currentWorkflow.scheduledFieldName,
                scheduledExecution: currentWorkflow.scheduledExecution,
                scheduledUnit: currentWorkflow.scheduledUnit,
                scheduledValue: currentWorkflow.scheduledValue,
              }
            }
          };
          await WorkflowApi.saveWorkflow(updatedPayload);
        } catch (err) {
          console.error("Error saving updated workflow config with attachment details:", err);
        }
      }
    }
  };

  const handleObjectChange = async (e) => {
    const newObjectName = e.target.value;

    // Save current object config to cache
    if (workflow.objectName) {
      setObjectActionCache((prevCache) => ({
        ...prevCache,
        [workflow.objectName]: {
          actions,
          criteriaList,
        },
      }));
    }

    // Update workflow object name
    setWorkflow((prev) => ({
      ...prev,
      objectName: newObjectName,
    }));

    setObjectFields([]);

    // Restore from cache if available
    if (objectActionCache[newObjectName]) {
      const cached = objectActionCache[newObjectName];
      let nextActions = cached.actions;
      if (workflow.triggerEvent === "before_save") {
        if (!nextActions?.length) {
          nextActions = [{ actionType: "Field Update", config: {} }];
        } else {
          const first = nextActions[0];
          nextActions = [
            {
              ...first,
              actionType: "Field Update",
              config: first.actionType === "Field Update" ? first.config : {},
            },
          ];
        }
      }
      setActions(nextActions);
      setCriteriaList(cached.criteriaList);
    } else {
      setActions(
        workflow.triggerEvent === "before_save"
          ? [{ actionType: "Field Update", config: {} }]
          : []
      );
      setCriteriaList([]);
    }

    // Fetch object fields
    if (newObjectName) {
      try {
        const response =
          await ObjectConfigApi.fetchConfigObjectByNameWithFields(
            newObjectName
          );
        if (response.success && response.data?.fields) {
          setObjectFields(response.data.fields);
        } else {
          setObjectFields([]);
        }
      } catch (err) {
        console.error("Error fetching fields for object", err);
        setObjectFields([]);
      }
    }
  };

  const toggleAttachment = (index) => {
    const updated = [...actions];

    if (updated[index].showAttachment) {
      // Trying to remove
      const confirmed = window.confirm(
        "Are you sure you want to remove the file rule?"
      );
      if (!confirmed) {
        return; // Exit without changing state if not confirmed
      }
      updated[index].showAttachment = false;
      updated[index].attachmentConfig = {}; // Optionally clear config
    } else {
      // Directly add without any message
      updated[index].showAttachment = true;
    }

    setActions(updated);
  };

  const renderAttachmentToggleButton = (index, action) => {
    if (readOnly) return null;

    return (
      <Button
        className="mt-2 mb-3 d-block"
        variant={action.showAttachment ? "outline-danger" : "outline-primary"}
        size="sm"
        onClick={() => toggleAttachment(index)}
      >
        {action.showAttachment
          ? "Remove Attachment Rule"
          : "Add Attachment Rule"}
      </Button>
    );
  };
  const handleSave = async () => {
    // Prevent duplicate workflow creation on double-click / rapid taps.
    // useRef is synchronous, so it guards even before React state updates.
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);

    setSaveSuccess(false);
    setSaveError("");

    if (!workflow.name || !workflow.objectName || !workflow.eventType || workflow.eventType.length === 0) {
      showError("Please fill all required workflow fields including at least one Event.");
      isSavingRef.current = false;
      setIsSaving(false);
      return;
    }

    if (
      savedTriggerEvent === "after_save" &&
      workflow.triggerEvent === "before_save"
    ) {
      showError(
        "This workflow was saved as After Save and cannot be changed to Before Save."
      );
      isSavingRef.current = false;
      setIsSaving(false);
      return;
    }

    const logicStr = String(workflow.logic ?? "");
    if (logicStr.length > WORKFLOW_LOGIC_MAX_LENGTH) {
      showError(
        `Criteria logic cannot be longer than ${WORKFLOW_LOGIC_MAX_LENGTH} characters (currently ${logicStr.length}).`
      );
      isSavingRef.current = false;
      setIsSaving(false);
      return;
    }

    for (const [index, action] of actions.entries()) {
      // Check if action type is selected
      if (!action.actionType || action.actionType.trim() === "") {
        showError(`Please select an action type for Action #${index + 1}`);
        isSavingRef.current = false;
        setIsSaving(false);
        return;
      }

      if (action.actionType === "Send Email") {
        const c = action.config;
        const hasSeq =
          Array.isArray(c?.emailRecipientSequence) &&
          c.emailRecipientSequence.length > 0;
        const { toEmails, configuredObjectEmailField, dynamicRecipients } =
          c || {};
        if (
          !hasSeq &&
          (!toEmails || toEmails.length === 0) &&
          (!configuredObjectEmailField ||
            String(configuredObjectEmailField).trim() === "") &&
          (!dynamicRecipients || dynamicRecipients.length === 0)
        ) {
          showError(
            `Provide at least one email recipient in Action #${index + 1}`
          );
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
      }

      if (action.actionType === "Send WhatsApp") {
        const error = validateEnclosedField(action.config.recipientField);
        console.log(
          "action.config.recipient_field: " + action.config.recipientField
        );
        console.log("error: " + error);
        if (error) {
          showError(`Action #${index + 1}: ${error}`);
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
      }

      if (action.actionType === "Create Record") {
        const targetObjectName = action.config?.targetObjectName;
        const fieldMappings = action.config?.fieldMappings;
        if (!targetObjectName) {
          showError(`Action #${index + 1}: Please select an object to create a record.`);
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
        if (!Array.isArray(fieldMappings) || fieldMappings.length === 0) {
          showError(`Action #${index + 1}: Please add at least one field/value mapping.`);
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
        const hasInvalid = fieldMappings.some((m) => !m?.fieldName || String(m.fieldName).trim() === "");
        if (hasInvalid) {
          showError(`Action #${index + 1}: Please select a field for each mapping row.`);
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
      }

      if (action.actionType === "Send Notification") {
        const title = (action.config?.title || "").trim();
        if (!title) {
          showError(`Action #${index + 1}: In-app notification requires a title.`);
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
        const hasHard =
          Array.isArray(action.config?.hardcodedRecipients) &&
          action.config.hardcodedRecipients.length > 0;
        const hasFields =
          Array.isArray(action.config?.recipientFieldTokens) &&
          action.config.recipientFieldTokens.length > 0;
        const hasOrder =
          Array.isArray(action.config?.recipientSequence) &&
          action.config.recipientSequence.length > 0;
        const hasIds =
          Array.isArray(action.config?.recipientUserIds) &&
          action.config.recipientUserIds.length > 0;
        const leg = action.config?.recipientUserId;
        const hasLegacy = leg != null && String(leg).trim() !== "";
        if (!hasHard && !hasFields && !hasOrder && !hasIds && !hasLegacy) {
          showError(
            `Action #${index + 1}: Add at least one fixed user and/or a user field from the record.`
          );
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
      }

      if (action.actionType === "Run Custom Script") {
        const id = (action.config?.customScriptId || "").trim();
        const uuidRe =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!id || !uuidRe.test(id)) {
          showError(
            `Action #${index + 1}: Select a custom script (backend or workflow).`
          );
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
      }

      if (action.actionType === "Field Update") {
        const cfg = action.config || {};
        let rows = [];
        if (Array.isArray(cfg.fieldUpdates) && cfg.fieldUpdates.length > 0) {
          rows = cfg.fieldUpdates;
        } else if (cfg.fieldName && String(cfg.fieldName).trim() !== "") {
          rows = [{ fieldUpdateKind: "standard", fieldName: cfg.fieldName, newValue: cfg.newValue }];
        }
        if (!rows.length) {
          showError(`Action #${index + 1}: Add at least one field update.`);
          isSavingRef.current = false;
          setIsSaving(false);
          return;
        }
        for (let j = 0; j < rows.length; j++) {
          const r = rows[j] || {};
          const kind = String(r.fieldUpdateKind || "standard").toLowerCase();
          const tgt = String(r.target || "self").toLowerCase();
          if (tgt === "parent") {
            if (!r.parentObjectName || !r.parentLookupFieldName) {
              showError(
                `Action #${index + 1}, Update #${j + 1}: Parent target requires a saved parent relationship.`
              );
              isSavingRef.current = false;
              setIsSaving(false);
              return;
            }
          }
          if (kind === "standard") {
            if (!r.fieldName || String(r.fieldName).trim() === "") {
              showError(`Action #${index + 1}, Update #${j + 1}: Select a field to update.`);
              isSavingRef.current = false;
              setIsSaving(false);
              return;
            }
          } else if (kind === "geocode") {
            const addrList = Array.isArray(r.geoAddressSourceFields)
              ? r.geoAddressSourceFields.filter((x) => x != null && String(x).trim() !== "")
              : [];
            const legacyAddr =
              r.geoAddressSourceField != null && String(r.geoAddressSourceField).trim() !== "";
            const coordMode =
              String(r.geoCoordinateTargetMode || "split").toLowerCase() === "combined"
                ? "combined"
                : "split";
            const missingAddr = addrList.length === 0 && !legacyAddr;
            const missingTargets =
              coordMode === "combined"
                ? !r.geoLatLngCombinedField || String(r.geoLatLngCombinedField).trim() === ""
                : !r.geoLatitudeField ||
                String(r.geoLatitudeField).trim() === "" ||
                !r.geoLongitudeField ||
                String(r.geoLongitudeField).trim() === "";
            if (missingAddr || missingTargets) {
              showError(
                coordMode === "combined"
                  ? `Action #${index + 1}, Update #${j + 1}: Geocode requires at least one address source field and a single target field for coordinates.`
                  : `Action #${index + 1}, Update #${j + 1}: Geocode requires at least one address source field, latitude field, and longitude field.`
              );
              isSavingRef.current = false;
              setIsSaving(false);
              return;
            }
          } else if (kind === "reverse_geocode") {
            const srcMode =
              String(r.geoCoordinateSourceMode || "split").toLowerCase() === "combined"
                ? "combined"
                : "split";
            const missingSrc =
              srcMode === "combined"
                ? !r.geoLatLngSourceField || String(r.geoLatLngSourceField).trim() === ""
                : !r.geoLatitudeSourceField ||
                String(r.geoLatitudeSourceField).trim() === "" ||
                !r.geoLongitudeSourceField ||
                String(r.geoLongitudeSourceField).trim() === "";
            const addrMode =
              String(r.geoAddressTargetMode || "formatted").toLowerCase() === "components"
                ? "components"
                : "formatted";
            const hasComponentTarget = [
              r.geoRevTargetFormattedField,
              r.geoRevTargetStreetField,
              r.geoRevTargetCityField,
              r.geoRevTargetStateField,
              r.geoRevTargetCountryField,
              r.geoRevTargetPostalField,
            ].some((x) => x != null && String(x).trim() !== "");
            const missingAddr =
              addrMode === "components"
                ? !hasComponentTarget
                : !r.geoAddressTargetField || String(r.geoAddressTargetField).trim() === "";
            if (missingSrc || missingAddr) {
              showError(
                `Action #${index + 1}, Update #${j + 1}: Reverse geocode needs coordinate source field(s) and at least one address target (single formatted field or one or more component fields).`
              );
              isSavingRef.current = false;
              setIsSaving(false);
              return;
            }
          }
        }
      }
    }

    // Clean up actions before saving - remove subject/body if templateId is present & strip staticFiles from JSON payload
    const cleanedActions = actions.map((action) => {
      let actionCopy = { ...action };

      if (actionCopy.actionType === "Send Email") {
        let config = { ...actionCopy.config };
        if (!config.emailRecipientSequence?.length) {
          const migrated = buildLegacyEmailSequence(config);
          if (migrated.length) {
            config = { ...config, emailRecipientSequence: migrated };
          }
        }
        if (config.templateId) {
          const { subject, body, ...restConfig } = config;
          config = restConfig;
        }
        actionCopy.config = config;
      }

      // Strip non-serializable File objects (staticFiles) from payload before sending JSON to API
      if (actionCopy.attachmentConfig?.staticFiles) {
        const { staticFiles, ...restAtt } = actionCopy.attachmentConfig;
        actionCopy.attachmentConfig = restAtt;
      }
      if (actionCopy.config?.attachmentConfig?.staticFiles) {
        const { staticFiles, ...restAtt } = actionCopy.config.attachmentConfig;
        actionCopy.config = {
          ...actionCopy.config,
          attachmentConfig: restAtt,
        };
      }

      return actionCopy;
    });

    const workflowConfig = {
      actions: cleanedActions,
      criteria: criteriaList,
      scheduling: {
        executionMode: workflow.executionMode,
        scheduledType: workflow.scheduledType,
        scheduledDateTime: workflow.scheduledDateTime,
        scheduledFieldName: workflow.scheduledFieldName,
        scheduledExecution: workflow.scheduledExecution,
        scheduledUnit: workflow.scheduledUnit,
        scheduledValue: workflow.scheduledValue,
      }
    };

    const payload = {
      id: workflow.id, // include ID for upsert
      name: workflow.name,
      objectName: workflow.objectName,
      eventType: workflow.eventType,
      triggerEvent: workflow.triggerEvent,
      isActive: workflow.isActive,
      logic: workflow.logic,
      config: workflowConfig,
    };

    console.log("Payload to Save:", payload);

    try {
      const response = await WorkflowApi.saveWorkflow(payload);
      console.log("Workflow saved successfully", response);
      setSaveSuccess(true);
      setSaveError("");

      // Get the workflow ID (either from response or existing)
      const savedWorkflowId = response.data?.id || workflow.id;

      // Optional: if new workflow, update the workflow state with returned id
      if (response.data?.id) {
        setWorkflow((prev) => ({ ...prev, id: response.data.id }));
      }

      // Upload pending attachments for actions that have static files
      // (files that were selected before workflow was saved)
      if (savedWorkflowId) {
        await uploadPendingAttachments(savedWorkflowId, actions, workflow);
      }

      triggerToast("Workflow saved successfully!", "success");

      navigate("/setup/workflows", {
        state: { successMessage: "Workflow saved successfully!" },
      });
      //handleCancel();
    } catch (error) {
      console.error("Error saving workflow", error);
      showError("Workflow could not be saved. Error: " + (error.message || error));
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!workflow.id) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this workflow?"
    );
    if (!confirmed) return;

    try {
      await WorkflowApi.deleteWorkflow(workflow.id);
      alert("Workflow deleted successfully.");
      navigate("/setup/workflows");
    } catch (error) {
      console.error("Failed to delete workflow:", error);
      alert("Failed to delete the workflow. Please try again.");
    }
  };

  const handleCancel = () => {
    if (workflow.id) {
      navigate(`/setup/workflows/${workflow.id}`);
    } else {
      navigate("/setup/workflows");
    }
  };
  const addAction = () => {
    if (workflow.triggerEvent === "before_save" && actions.length >= 1) return;
    const newIndex = actions.length;
    const defaultAction =
      workflow.triggerEvent === "before_save"
        ? { actionType: "Field Update", config: {} }
        : { actionType: "", config: {} };
    setActions((prev) => [...prev, defaultAction]);
    // Expand the newly added action by default
    setExpandedActions((prev) => ({ ...prev, [newIndex]: true }));
  };

  const toggleAction = (index) => {
    setExpandedActions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const deleteAction = (index) => {
    setActions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAction = (index, field, value) => {
    const updated = [...actions];
    updated[index][field] = value;
    if (field === "actionType") {
      updated[index].config = {};
    }
    setActions(updated);
  };

  const updateActionConfig = (index, key, value) => {
    setActions((prev) => {
      const next = [...prev];
      const cur = next[index];
      if (!cur) return prev;
      next[index] = {
        ...cur,
        config: {
          ...cur.config,
          [key]: value,
        },
      };
      return next;
    });
  };

  /** Merge several config keys in one setState (avoids dropping e.g. recipientFieldTokens). */
  const patchActionConfig = (index, partial) => {
    if (!partial || typeof partial !== "object") return;
    setActions((prev) => {
      const next = [...prev];
      const cur = next[index];
      if (!cur) return prev;
      next[index] = {
        ...cur,
        config: {
          ...cur.config,
          ...partial,
        },
      };
      return next;
    });
  };

  const renderActionConfigForm = (action, index) => {
    switch (action.actionType) {
      case "Create Task":
        return (
          <>
            <WorkflowCreateTask
              config={action.config}
              onChange={(field, value) =>
                updateActionConfig(index, field, value)
              }
              onConfigPatch={(partial) => patchActionConfig(index, partial)}
              objectName={workflow.objectName}
              readOnly={readOnly}
            />
            {renderAttachmentToggleButton(index, action)}

            {action.showAttachment && (
              <WorkflowAttachmentSelector
                config={action.attachmentConfig || {}}
                onChange={(field, value) =>
                  updateAttachmentConfig(index, field, value)
                }
                readOnly={readOnly}
                eventType={workflow.eventType}
                workflowId={workflow.id}
                actionIndex={index}
              />
            )}
          </>
        );

      case "Send Email":
        return (
          <>
            <WorkflowSendEmail
              config={action.config}
              onChange={(field, value) =>
                updateActionConfig(index, field, value)
              }
              onConfigPatch={(partial) => patchActionConfig(index, partial)}
              objectName={workflow.objectName}
              readOnly={readOnly}
            />
            {renderAttachmentToggleButton(index, action)}

            {action.showAttachment && (
              <WorkflowAttachmentSelector
                config={action.attachmentConfig || {}}
                onChange={(field, value) =>
                  updateAttachmentConfig(index, field, value)
                }
                readOnly={readOnly}
                eventType={workflow.eventType}
                workflowId={workflow.id}
                actionIndex={index}
              />
            )}
          </>
        );

      case "Send WhatsApp":
        return (
          <>
            <WorkflowSendWhatsApp
              config={action.config}
              onChange={(field, value) =>
                updateActionConfig(index, field, value)
              }
              objectName={workflow.objectName}
              readOnly={readOnly}
              workflowId={workflow.id}
              actionIndex={index}
              eventType={workflow.eventType}
            />
            {/*renderAttachmentToggleButton(index, action)*/}

            {action.showAttachment && (
              <WorkflowAttachmentSelector
                config={action.attachmentConfig || {}}
                onChange={(field, value) =>
                  updateAttachmentConfig(index, field, value)
                }
                readOnly={readOnly}
                eventType={workflow.eventType}
                workflowId={workflow.id}
                actionIndex={index}
                requiredMediaType={action.config?.templateHeader || action.config?.header}
              />
            )}
          </>
        );

      case "Field Update":
        return (
          <>
            <WorkflowFieldUpdate
              config={action.config}
              onChange={(field, value) =>
                updateActionConfig(index, field, value)
              }
              objectName={workflow.objectName}
              readOnly={readOnly}
            />
          </>
        );

      case "Send Notification":
        return (
          <WorkflowSendNotification
            config={action.config}
            onChange={(field, value) => updateActionConfig(index, field, value)}
            onConfigPatch={(partial) => patchActionConfig(index, partial)}
            objectName={workflow.objectName}
            readOnly={readOnly}
          />
        );

      case "Create Record":
        return (
          <WorkflowCreateRecord
            config={action.config}
            onChange={(field, value) => updateActionConfig(index, field, value)}
            objectConfigs={objectConfigs}
            sourceObjectName={workflow.objectName}
            readOnly={readOnly}
          />
        );

      case "Run Custom Script":
        return (
          <WorkflowRunCustomScript
            config={action.config}
            onChange={(field, value) => updateActionConfig(index, field, value)}
            onConfigPatch={(partial) => patchActionConfig(index, partial)}
            objectName={workflow.objectName}
            readOnly={readOnly}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <AppToast
        show={toast.show}
        message={toast.message}
        variant={toast.variant}
        sticky={toast.variant === "danger"}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />
      <Container fluid className="py-4">
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                {/* Back Button */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigate('/setup/workflows')}
                  className="mb-3"
                >
                  <FaArrowLeft className="me-2" />
                  Back to Workflows
                </Button>

                {/* Title Section */}
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="bg-warning bg-opacity-10 p-2 rounded-circle d-inline-flex me-3">
                      <FaBolt size={24} className="text-warning" />
                    </div>
                    <div>
                      <h3 className="mb-0 fw-bold">
                        {workflow.id ? (readOnly ? 'View Workflow' : 'Edit Workflow') : 'Create New Workflow'}
                      </h3>
                      {workflow.name && (
                        <p className="text-dark mb-0 mt-1">{workflow.name}</p>
                      )}
                    </div>
                  </div>
                  {readOnly && (
                    <div>
                      <Button
                        variant="primary"
                        onClick={() => navigate(`/setup/workflows/${id}/edit`)}
                        className="me-2"
                      >
                        <FaEdit className="me-2" />
                        Edit
                      </Button>
                      <Button variant="danger" onClick={handleDeleteWorkflow}>
                        <FaTrash className="me-2" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={12} xl={12} className="mx-auto">
            {saveSuccess && !readOnly && (
              <Alert variant="success" className="mb-3">
                <FaInfoCircle className="me-2" />
                Workflow saved successfully!
              </Alert>
            )}



            <Form
              noValidate
              validated={validated}
              onSubmit={handleSubmit}
            >
              {/* Workflow Configuration Card */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">Workflow Configuration</h5>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold" style={standardLabelStyles}>Workflow Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={workflow.name}
                          onChange={(e) =>
                            setWorkflow((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          required={!readOnly}
                          disabled={readOnly}
                          placeholder="Enter workflow name"
                          style={standardInputStyles}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold" style={standardLabelStyles}>Object Name <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={workflow.objectName}
                          onChange={handleObjectChange}
                          required={!readOnly}
                          disabled={readOnly}
                          style={standardInputStyles}
                        >
                          <option value="">Select Object</option>
                          {objectConfigs?.map((config) => (
                            <option key={config.id} value={config.object_name}>
                              {config.object_label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold" style={standardLabelStyles}>Criteria Logic</Form.Label>
                        {(() => {
                          const validWorkflowCriteriaCount = criteriaList.filter(
                            (c) => c.fieldName && c.operator
                          ).length;
                          const workflowLogicSelectValue =
                            isSimpleWorkflowCriteriaLogic(workflow.logic)
                              ? String(workflow.logic).trim().toUpperCase()
                              : "CUSTOM";
                          const onWorkflowLogicModeChange = (e) => {
                            const v = e.target.value;
                            if (v === "CUSTOM") {
                              setWorkflow((prev) => ({
                                ...prev,
                                logic: defaultWorkflowCustomCriteriaExpression(
                                  validWorkflowCriteriaCount
                                ),
                              }));
                            } else {
                              setWorkflow((prev) => ({ ...prev, logic: v }));
                            }
                          };
                          return (
                            <>
                              <Form.Select
                                value={workflowLogicSelectValue}
                                onChange={onWorkflowLogicModeChange}
                                disabled={readOnly}
                                style={standardInputStyles}
                              >
                                <option value="AND">AND (all criteria must be true)</option>
                                <option value="OR">OR (any criterion can be true)</option>
                                <option value="CUSTOM">Custom (e.g. (1 OR 2) AND 3)</option>
                              </Form.Select>
                              {workflowLogicSelectValue === "CUSTOM" && (
                                <div className="mt-2">
                                  <Form.Label className="small text-muted mb-1">
                                    Custom expression
                                  </Form.Label>
                                  {(() => {
                                    const logicStr = String(workflow.logic ?? "");
                                    const logicLen = logicStr.length;
                                    const overLimit = logicLen > WORKFLOW_LOGIC_MAX_LENGTH;
                                    return (
                                      <>
                                        <Form.Control
                                          as="textarea"
                                          rows={2}
                                          value={logicStr}
                                          onChange={(e) =>
                                            setWorkflow((prev) => ({
                                              ...prev,
                                              logic: e.target.value,
                                            }))
                                          }
                                          disabled={readOnly}
                                          placeholder="(1 OR 2) AND 3"
                                          className="font-monospace small"
                                          style={standardInputStyles}
                                          isInvalid={overLimit}
                                        />
                                        <Form.Text className={overLimit ? "text-danger" : "text-muted"}>
                                          {overLimit ? (
                                            <>
                                              Criteria logic cannot exceed {WORKFLOW_LOGIC_MAX_LENGTH}{" "}
                                              characters ({logicLen} / {WORKFLOW_LOGIC_MAX_LENGTH}). Shorten the
                                              expression before saving.
                                            </>
                                          ) : (
                                            <>
                                              {logicLen} / {WORKFLOW_LOGIC_MAX_LENGTH} characters. Use 1, 2, 3… for each
                                              criterion row below, with AND, OR, and parentheses. Same rules as list view
                                              filters.
                                            </>
                                          )}
                                        </Form.Text>
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </Form.Group>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold" style={standardLabelStyles}>Event Type <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={workflow.triggerEvent}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (
                              blockBeforeSaveOnEdit &&
                              next === "before_save"
                            ) {
                              return;
                            }
                            setWorkflow((prev) => ({
                              ...prev,
                              triggerEvent: next,
                            }));
                          }}
                          disabled={readOnly}
                          required={!readOnly}
                          style={standardInputStyles}
                        >
                          <option
                            value="before_save"
                            disabled={blockBeforeSaveOnEdit}
                          >
                            Before Save
                          </option>
                          <option value="after_save">After Save</option>
                        </Form.Select>
                        {blockBeforeSaveOnEdit && !readOnly && (
                          <Form.Text className="text-muted mt-1 d-block">
                            This workflow was saved as After Save. Before Save cannot be selected.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold" style={standardLabelStyles}>Event <span className="text-danger">*</span></Form.Label>
                        <MultiSelectDropdown
                          selectedValues={workflow.eventType}
                          picklistValues={[
                            { key: "onCreate", value: "On Create" },
                            { key: "onUpdate", value: "On Update" },
                          ]}
                          onChange={(selectedValues) => {
                            setWorkflow((prev) => ({
                              ...prev,
                              eventType: selectedValues,
                            }));
                          }}
                          disabled={readOnly}
                          placeholder="Select Event(s)"
                          isRequired={!readOnly}
                        />
                        {workflow.eventType.length === 0 && (
                          <Form.Text className="text-muted mt-1 d-block">
                            Select at least one event
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>



                    <Col md={2} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold" style={standardLabelStyles}>Status</Form.Label>
                        <div className="d-flex align-items-center mt-2">
                          <Form.Check
                            type="switch"
                            id="isActive-switch"
                            label={
                              <Badge bg={workflow.isActive ? "success" : "secondary"}>
                                {workflow.isActive ? "Active" : "Inactive"}
                              </Badge>
                            }
                            checked={workflow.isActive}
                            onChange={(e) =>
                              setWorkflow((prev) => ({
                                ...prev,
                                isActive: e.target.checked,
                              }))
                            }
                            disabled={readOnly}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Execution Mode Section */}
                  <Row className="mt-3">
                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold" style={standardLabelStyles}>Execution Mode <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={workflow.executionMode}
                          onChange={(e) =>
                            setWorkflow((prev) => ({
                              ...prev,
                              executionMode: e.target.value,
                            }))
                          }
                          disabled={readOnly}
                          required={!readOnly}
                          style={standardInputStyles}
                        >
                          <option value="immediate">Immediate (Execute when event fires)</option>
                          <option value="scheduled">Scheduled (Execute at specific time)</option>
                        </Form.Select>
                        <Form.Text className="text-muted mt-1 d-block">
                          {workflow.executionMode === "immediate"
                            ? "Workflow actions will execute immediately when criteria are met"
                            : "Workflow actions will be scheduled to execute at a specific date/time"}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Scheduling Configuration - Only show if scheduled mode */}
                  {workflow.executionMode === "scheduled" && (
                    <Row className="mt-3">
                      <Col md={12} className="mb-3">
                        <Card className="border" style={{ backgroundColor: "#f8f9fa" }}>
                          <Card.Body>
                            <h6 className="fw-semibold mb-3">Schedule Configuration</h6>

                            {/* Reference Field */}
                            <Row className="mb-3">
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold" style={standardLabelStyles}>Reference Field</Form.Label>
                                  <Form.Select
                                    value={workflow.scheduledFieldName}
                                    onChange={(e) =>
                                      setWorkflow((prev) => ({
                                        ...prev,
                                        scheduledFieldName: e.target.value,
                                      }))
                                    }
                                    disabled={readOnly || !workflow.objectName}
                                    style={standardInputStyles}
                                  >
                                    <option value="">-- Select Field --</option>
                                    {objectFields
                                      .filter(
                                        (field) =>
                                          field.data_type === "date" ||
                                          field.data_type === "datetime"
                                      )
                                      .map((field) => (
                                        <option key={field.id} value={field.field_name}>
                                          {field.field_label || field.field_name}
                                        </option>
                                      ))}
                                  </Form.Select>
                                  <Form.Text className="text-muted mt-1 d-block">
                                    Select a date/datetime field from the record
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                            </Row>

                            {/* Execution, Unit, and Value in same row */}
                            <Row className="mb-3">
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold" style={standardLabelStyles}>Execution</Form.Label>
                                  <Form.Select
                                    value={workflow.scheduledExecution}
                                    onChange={(e) =>
                                      setWorkflow((prev) => ({
                                        ...prev,
                                        scheduledExecution: e.target.value,
                                      }))
                                    }
                                    disabled={readOnly}
                                    style={standardInputStyles}
                                  >
                                    <option value="after">After</option>
                                    <option value="before">Before</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold" style={standardLabelStyles}>Unit</Form.Label>
                                  <Form.Select
                                    value={workflow.scheduledUnit}
                                    onChange={(e) =>
                                      setWorkflow((prev) => ({
                                        ...prev,
                                        scheduledUnit: e.target.value,
                                      }))
                                    }
                                    disabled={readOnly}
                                    style={standardInputStyles}
                                  >
                                    <option value="minutes">Minutes</option>
                                    <option value="hours">Hours</option>
                                    <option value="days">Days</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold" style={standardLabelStyles}>Value</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="1"
                                    value={workflow.scheduledValue}
                                    onChange={(e) =>
                                      setWorkflow((prev) => ({
                                        ...prev,
                                        scheduledValue: e.target.value,
                                      }))
                                    }
                                    disabled={readOnly}
                                    placeholder="Enter number"
                                    style={standardInputStyles}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            {/* Helper text for Based on Field option */}
                            {workflow.scheduledType === "based_on_field" && (
                              <Row>
                                <Col md={12}>
                                  <Form.Text className="text-muted">
                                    Execute {workflow.scheduledExecution} the reference field date by the specified value
                                  </Form.Text>
                                </Col>
                              </Row>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>

              {/* Criteria Configuration Card */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">Workflow Criteria</h5>
                  <small className="text-dark">Define conditions that trigger this workflow</small>
                </Card.Header>
                <Card.Body className="p-4">
                  <WorkflowCriteriaConfig
                    criteriaList={criteriaList}
                    onAdd={addCriteria}
                    onDelete={deleteCriteria}
                    onChange={updateCriteria}
                    onPatch={patchCriteria}
                    objectFields={objectFields || []}
                    valueFields={criteriaValueFields?.length ? criteriaValueFields : objectFields}
                    readOnly={readOnly}
                    disabled={!workflow.objectName}
                    eventType={workflow.eventType}
                    triggerEvent={workflow.triggerEvent}
                  />
                </Card.Body>
              </Card>

              {/* Actions Card */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-bottom">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-0 fw-semibold">Workflow Actions</h5>
                      <small className="text-dark">Define what happens when workflow criteria are met</small>
                      {isBeforeSaveWorkflow && (
                        <div className="text-muted small mt-1">
                          Before Save allows one <strong>Field Update</strong> action only.
                        </div>
                      )}
                    </div>
                    {!readOnly && !isBeforeSaveWorkflow && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={addAction}
                        disabled={!workflow.objectName}
                        title={
                          !workflow.objectName
                            ? "Please select an object first"
                            : ""
                        }
                      >
                        <FaPlus className="me-2" size={12} />
                        Add Action
                      </Button>
                    )}
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  {actions?.length === 0 && (
                    <div className="text-center py-5 text-dark">
                      <FaBolt size={48} className="mb-3 " />
                      <p>
                        {!workflow.objectName
                          ? "Please select an object first to add actions."
                          : isBeforeSaveWorkflow
                            ? "Configuring Field Update for Before Save…"
                            : "No actions configured yet. Click \"Add Action\" to get started."
                        }
                      </p>
                    </div>
                  )}

                  {actions?.map((action, index) => {
                    const isExpanded = expandedActions[index] === true; // Default to collapsed
                    const actionForRender = isBeforeSaveWorkflow
                      ? { ...action, actionType: "Field Update" }
                      : action;
                    return (
                      <Card className="mb-3 border" key={index}>
                        <Card.Header
                          className="bg-light"
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleAction(index)}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="me-2" style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                minWidth: '20px',
                                color: '#212529'
                              }}>
                                {isExpanded ? (
                                  <FaChevronDown size={16} style={{ color: 'inherit' }} />
                                ) : (
                                  <FaChevronUp size={16} style={{ color: 'inherit' }} />
                                )}
                              </div>
                              <h6 className="mb-0 text-dark">
                                Action #{index + 1}
                                {actionForRender.actionType && (
                                  <Badge bg="secondary" className="ms-2">
                                    {actionForRender.actionType}
                                  </Badge>
                                )}
                              </h6>
                            </div>
                            <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              {!readOnly && !isBeforeSaveWorkflow && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => deleteAction(index)}
                                >
                                  <FaTrash className="me-1" size={12} />
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        </Card.Header>
                        <Collapse in={isExpanded}>
                          <div>
                            <Card.Body>
                              <Row>
                                <Col>
                                  <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Action Type <span className="text-danger">*</span></Form.Label>
                                    {isBeforeSaveWorkflow ? (
                                      <Form.Control
                                        type="text"
                                        value="Field Update"
                                        readOnly
                                        disabled
                                        style={standardInputStyles}
                                      />
                                    ) : (
                                      <Form.Select
                                        value={action.actionType}
                                        onChange={(e) =>
                                          updateAction(
                                            index,
                                            "actionType",
                                            e.target.value
                                          )
                                        }
                                        disabled={readOnly}
                                        required
                                      >
                                        <option value="">Select Action</option>
                                        {actionTypeOptions?.map((type) => (
                                          <option key={type} value={type}>
                                            {type}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    )}
                                  </Form.Group>

                                  {renderActionConfigForm(actionForRender, index)}
                                </Col>
                              </Row>
                            </Card.Body>
                          </div>
                        </Collapse>
                      </Card>
                    );
                  })}
                </Card.Body>
              </Card>

              {/* Action Buttons */}
              {!readOnly && (
                <div className="d-flex justify-content-between align-items-center py-3">
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    size="lg"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Workflow
                      </>
                    )}
                  </Button>
                </div>
              )}
            </Form>
          </Col>
        </Row>
      </Container >

    </>
  );
}
